// API utilities and error handling
export class ApiUtils {
    /**
     * Standardize API responses
     */
    static createResponse(success, data, error, code) {
        return {
            success,
            data,
            error,
            code
        };
    }
    /**
     * Handle Supabase errors and convert to standardized format
     */
    static handleSupabaseError(error) {
        if (!error) {
            return { error: 'Unknown error occurred' };
        }
        // Common Supabase error codes
        const errorMappings = {
            '23505': 'A record with this information already exists',
            '23503': 'Referenced record does not exist',
            '42501': 'Insufficient permissions',
            'PGRST116': 'No records found',
            'PGRST301': 'Invalid request format'
        };
        const code = error.code || error.error_code;
        const message = errorMappings[code] || error.message || 'An unexpected error occurred';
        console.error('Supabase error:', { code, message, details: error });
        return {
            error: message,
            code
        };
    }
    /**
     * Validate email format
     */
    static isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    /**
     * Validate phone number format (US)
     */
    static isValidPhoneNumber(phone) {
        const phoneRegex = /^\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/;
        return phoneRegex.test(phone);
    }
    /**
     * Validate zip code format (US)
     */
    static isValidZipCode(zipCode) {
        const zipRegex = /^\d{5}(-\d{4})?$/;
        return zipRegex.test(zipCode);
    }
    /**
     * Sanitize and validate user input
     */
    static sanitizeInput(input) {
        return input
            .trim()
            .replace(/[<>]/g, '') // Remove potential XSS characters
            .substring(0, 1000); // Limit length
    }
    /**
     * Generate pagination metadata
     */
    static generatePagination(page, limit, total) {
        const totalPages = Math.ceil(total / limit);
        return {
            page,
            limit,
            total,
            totalPages,
            hasNext: page < totalPages,
            hasPrev: page > 1
        };
    }
    /**
     * Retry function with exponential backoff
     */
    static async retry(fn, maxRetries = 3, baseDelay = 1000) {
        let lastError;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await fn();
            }
            catch (error) {
                lastError = error;
                if (attempt === maxRetries) {
                    throw error;
                }
                // Exponential backoff
                const delay = baseDelay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        throw lastError;
    }
    /**
     * Format date for API responses
     */
    static formatDate(date) {
        const d = typeof date === 'string' ? new Date(date) : date;
        return d.toISOString();
    }
    /**
     * Calculate BMI
     */
    static calculateBMI(weightPounds, heightInches) {
        return Math.round(((weightPounds / (heightInches * heightInches)) * 703) * 10) / 10;
    }
    /**
     * Convert height to inches
     */
    static heightToInches(feet, inches) {
        return (feet * 12) + inches;
    }
    /**
     * Format currency for display
     */
    static formatCurrency(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }
    /**
     * Mask sensitive data for logging
     */
    static maskSensitiveData(data) {
        if (!data || typeof data !== 'object') {
            return data;
        }
        const sensitiveFields = ['password', 'ssn', 'credit_card', 'token', 'secret'];
        const masked = { ...data };
        for (const key in masked) {
            if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
                masked[key] = '***MASKED***';
            }
            else if (key.toLowerCase().includes('email')) {
                // Partially mask email
                const email = masked[key];
                if (typeof email === 'string' && email.includes('@')) {
                    const [username, domain] = email.split('@');
                    masked[key] = `${username.substring(0, 2)}***@${domain}`;
                }
            }
            else if (typeof masked[key] === 'object') {
                masked[key] = this.maskSensitiveData(masked[key]);
            }
        }
        return masked;
    }
    /**
     * Generate unique ID for tracking
     */
    static generateTrackingId() {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Validate required fields
     */
    static validateRequiredFields(data, requiredFields) {
        const missingFields = requiredFields.filter(field => {
            const value = data[field];
            return value === undefined || value === null || value === '';
        });
        return {
            isValid: missingFields.length === 0,
            missingFields
        };
    }
    /**
     * Sleep utility for delays
     */
    static sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
/**
 * Logger utility for consistent logging across services
 */
export class Logger {
    static context = 'SharedServices';
    static setContext(context) {
        this.context = context;
    }
    static info(message, data) {
        console.log(`[${this.context}] INFO: ${message}`, data ? ApiUtils.maskSensitiveData(data) : '');
    }
    static warn(message, data) {
        console.warn(`[${this.context}] WARN: ${message}`, data ? ApiUtils.maskSensitiveData(data) : '');
    }
    static error(message, error, data) {
        console.error(`[${this.context}] ERROR: ${message}`, {
            error: error?.message || error,
            stack: error?.stack,
            data: data ? ApiUtils.maskSensitiveData(data) : undefined
        });
    }
    static debug(message, data) {
        const nodeEnv = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'development';
        if (nodeEnv === 'development') {
            console.debug(`[${this.context}] DEBUG: ${message}`, data ? ApiUtils.maskSensitiveData(data) : '');
        }
    }
}
/**
 * Rate limiting utility for API calls
 */
export class RateLimiter {
    static requests = new Map();
    static async checkLimit(key, maxRequests, windowMs) {
        const now = Date.now();
        const requests = this.requests.get(key) || [];
        // Clean old requests outside window
        const validRequests = requests.filter(time => now - time < windowMs);
        if (validRequests.length >= maxRequests) {
            const oldestRequest = Math.min(...validRequests);
            const resetTime = oldestRequest + windowMs;
            return { allowed: false, resetTime };
        }
        // Add current request
        validRequests.push(now);
        this.requests.set(key, validRequests);
        return { allowed: true };
    }
    static reset(key) {
        this.requests.delete(key);
    }
}
